import { SITE_NAME, absoluteUrl } from '@/lib/config';

/**
 * Structured data for the content pages. Every helper returns a plain object for `<Seo jsonLd>`;
 * nothing is invented — fields whose value is unknown are omitted rather than guessed.
 */

export interface BreadcrumbItem {
  name: string;
  /** Site path (`/where-to-use/kitchen`). */
  path: string;
}

export function breadcrumbJsonLd(items: readonly BreadcrumbItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function faqJsonLd(items: readonly FaqItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

export interface OrganizationFacts {
  /** From the public site settings; `null`/missing values are left out. */
  phone?: string | null | undefined;
  email?: string | null | undefined;
  address?: string | null | undefined;
  social?: Readonly<Record<string, string>> | undefined;
}

/** A multi-line address on one line ("…Baggad,\nBaggad-Bhim" → "…Baggad, Baggad-Bhim"): `streetAddress` is one string. */
const oneLine = (address: string): string => address.replace(/,?[^\S\n]*\n\s*/g, ', ');

/**
 * https links only — structured data is crawled, so it must never carry a `javascript:` or `data:` URL — and
 * only a *profile*: the bare home page of a network (`https://www.instagram.com/`, what the site links to until
 * the firm's real profiles are known, D-318) says nothing about this business and must not be claimed as one.
 */
const profileUrl = (value: string | undefined): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.pathname.replace(/\/+$/, '') !== '' ? value : null;
  } catch {
    return null;
  }
};

/**
 * The parts of the registered address the owner gave (the Legal Disclaimer, clause 13: E-37, RIICO Industrial Area, Baggad,
 * Baggad-Bhim, Rajsamand 313341, Rajasthan, India). They are facts of the firm, so they sit here as fixed fields next to the
 * street line from the settings — search engines read the locality, region and postal code, not the street line.
 */
const ADDRESS_PARTS = {
  addressLocality: 'Rajsamand',
  addressRegion: 'Rajasthan',
  postalCode: '313341',
  addressCountry: 'IN',
} as const;

/** What the firm sells. Taken from the owner's brief; no prices (they are given on enquiry), counts, ratings or years. */
const OFFERS = [
  'Granite slabs',
  'Granite tiles',
  'Granite countertops',
  'Landscape and paving stone',
] as const;

/** The search phrases the page is about (D-353): what the firm sells, its colours and its finish. */
const KNOWS_ABOUT: readonly string[] = [
  ...OFFERS,
  'Kitchen granite',
  'Polished granite',
  'Natural stone',
  'Black granite',
  'Red granite',
  'White granite',
  'Pink granite',
  'Brown granite',
];

/**
 * `Organization` — or `LocalBusiness` once a street address is known (Google wants an address for a
 * local business). The name, the site URL, a plain description of what the firm sells and the settings values
 * that exist are included; nothing is invented (no ratings, reviews, founding year, certificates or prices).
 */
export function organizationJsonLd(facts: OrganizationFacts = {}): Record<string, unknown> {
  const address = facts.address?.trim();
  const phone = facts.phone?.trim();
  const email = facts.email?.trim();
  const sameAs = Object.values(facts.social ?? {})
    .map((value) => profileUrl(value))
    .filter((value): value is string => value !== null);

  return {
    '@context': 'https://schema.org',
    '@type': address ? 'LocalBusiness' : 'Organization',
    '@id': `${absoluteUrl('/')}#organization`,
    name: SITE_NAME,
    legalName: 'M/s Shree Balaji Granites',
    url: absoluteUrl('/'),
    logo: absoluteUrl('/logo/sbg-logo.png'),
    description:
      'Granite manufacturer and supplier in Rajsamand, Rajasthan, India: slabs, tiles, countertops and landscape stone in many colours.',
    keywords: KNOWS_ABOUT.join(', ').toLowerCase(),
    knowsAbout: KNOWS_ABOUT,
    areaServed: { '@type': 'Country', name: 'India' },
    makesOffer: OFFERS.map((name) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Product', name },
    })),
    ...(phone ? { telephone: phone } : {}),
    ...(email ? { email } : {}),
    ...(address
      ? {
          address: {
            '@type': 'PostalAddress',
            streetAddress: oneLine(address),
            // only while the address is still the registered one (the postal code is the witness): another address would be wrong
            ...(address.includes(ADDRESS_PARTS.postalCode) ? ADDRESS_PARTS : {}),
          },
        }
      : {}),
    ...(phone || email
      ? {
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'sales',
            areaServed: 'IN',
            availableLanguage: 'English',
            ...(phone ? { telephone: phone } : {}),
            ...(email ? { email } : {}),
          },
        }
      : {}),
    ...(sameAs.length > 0 ? { sameAs } : {}),
  };
}
